/**
 * The shared **case generator** (research/taxonomy.md §3): an outer box (two sides, top,
 * bottom, optional plinth, back none · overlay · rebated) holding columns separated by
 * full-height dividers; each column holds rows bottom-up (the clear height between
 * horizontals), and each row may hold a list of short dividers (cell clear widths).
 *
 * - grid  = columns with their own rows, no row dividers (column-major);
 * - offset-grid = one column, full-width rows, per-row divider lists (row-major);
 * - cube  = one column whose rows all carry the same divider list (later: merged into
 *   continuous dividers with egg-crate joints).
 *
 * Pure and synchronous; everything is in 0.1 mm ticks. Families decide names, stock and
 * validation; this module only lays out boxes and the joints between them.
 */
import type { JointKind } from './assembly';
import { slatLayout, type BaseSpec, type SlatSpec } from './base';

export type BackKind = 'none' | 'overlay' | 'rebated';

export interface CaseRow {
  /** Clear height of the opening (ticks). */
  clear: number;
  /** Cell clear widths when the row is divided (short dividers between them); omit for one cell. */
  cells?: number[];
}

export interface CaseColumn {
  /** Clear width between the verticals either side (ticks). */
  clear: number;
  /** Bottom-up. There is one horizontal (shelf) between consecutive rows. */
  rows: CaseRow[];
}

export interface CaseNaming {
  shelf: (column: number, k: number, columns: number) => { id: string; label: string };
  divider: (k: number, columns: number) => { id: string; label: string };
  rowDivider: (column: number, row: number, k: number) => { id: string; label: string };
  back: (k: number, pieces: number) => { id: string; label: string };
  opening: (column: number, row: number, cell: number, columns: number) => { id: string; label: string };
}

export const DEFAULT_NAMING: CaseNaming = {
  shelf: (c, k, n) => (n === 1 ? { id: `shelf-${k}`, label: `Shelf ${k}` } : { id: `shelf-${c + 1}-${k}`, label: `Bay ${c + 1} shelf ${k}` }),
  divider: (k) => ({ id: `divider-${k}`, label: `Divider ${k}` }),
  rowDivider: (c, r, k) => ({ id: `rowdiv-${c + 1}-${r + 1}-${k}`, label: `Row ${r + 1} divider ${k}` }),
  back: (k, n) => (n === 1 ? { id: 'back', label: 'Back' } : { id: `back-${k}`, label: `Back ${k} of ${n}` }),
  opening: (c, r, cell, n) => ({
    id: n === 1 ? `opening-${r + 1}${cell ? `-${cell + 1}` : ''}` : `opening-${c + 1}-${r + 1}${cell ? `-${cell + 1}` : ''}`,
    label: n === 1 ? `Opening ${r + 1}` : `Bay ${c + 1} opening ${r + 1}`,
  }),
};

export interface CaseInput {
  W: number;
  H: number;
  D: number;
  /** Carcass thickness (ticks). */
  t: number;
  back: { kind: BackKind; thickness: number };
  /** Plinth (kick board) height, 0 for none (ignored when `base` is given). */
  plinth: number;
  /**
   * What the case stands on (FND-08). Omitted: a plinth of `plinth` height flush with the front,
   * or the floor — exactly the layout every existing design and golden has.
   */
  base?: BaseSpec;
  /** Slatted outer sides (FND-13); omitted or null for plain side panels. */
  slats?: SlatSpec | null;
  /**
   * How fixed shelves meet the verticals. Housed shelves run into a dado one third of t deep each
   * end; through-tenoned shelves (thick solid frames, FND-23) run right through the sides (+ t each end).
   */
  shelfJoint: Extract<JointKind, 'screwed-butt' | 'housed-dado' | 'dowel-butt' | 'through-tenon'>;
  /** How the top and bottom meet the sides: screwed butt (default) or mitred (thick solid frames). */
  corner?: 'butt' | 'mitre';
  rowDividerJoint?: Extract<JointKind, 'screwed-butt' | 'housed-dado' | 'dowel-butt'>;
  columns: CaseColumn[];
  /**
   * Splits the back into pieces at divider centrelines (fewest pieces, left to right) when
   * `fits(widthTicks, heightTicks)` says one piece is too big. Omit to keep one piece.
   */
  backFits?: (w: number, h: number) => boolean;
  naming?: CaseNaming;
}

export interface CaseBox { x: number; y: number; z: number; sx: number; sy: number; sz: number }

export type CaseRole = 'side' | 'top' | 'bottom' | 'shelf' | 'divider' | 'back' | 'plinth' | 'slat' | 'rail' | 'post';

export interface CaseBoard {
  id: string;
  role: CaseRole;
  label: string;
  /** Along the grain, across it, thickness (ticks). */
  l: number;
  w: number;
  t: number;
  grainWorld: 'x' | 'y' | 'z';
  box: CaseBox;
  /** 'carcass' boards are cut from the carcass stock, 'back' from the back sheet. */
  stock: 'carcass' | 'back';
  /** Horizontals: underside from the floor (ticks). */
  datum?: number;
  shelfNo?: number;
  column?: number;
  /** The rebate or housing this board carries, for the edge/face text. */
  machining?: string;
}

export interface CaseJoint { a: string; b: string; kind: JointKind; face: string; datum: number }

export interface CaseOpening {
  id: string;
  label: string;
  column: number;
  row: number;
  cell: number;
  x0: number;
  x1: number;
  y0: number;
  y1: number;
  clear: number;
}

export interface CaseOutput {
  boards: CaseBoard[];
  joints: CaseJoint[];
  openings: CaseOpening[];
  /** Shelf undersides from the floor (ticks), per column, bottom-up. */
  datums: { partId: string; label: string; ticks: number; column: number; shelfNo: number }[];
  /** x of each column's left and right clear edge (ticks). */
  columnSpans: { x0: number; x1: number }[];
  /** The clear interior: top face of the bottom to the underside of the top. */
  interior: { y0: number; y1: number; z0: number; z1: number };
}

/** Housing depth for a housed dado: one third of the board thickness (plan §5). */
export const housingDepth = (t: number): number => Math.round(t / 3);

/**
 * The case on its base: a plinth set back from the front, the bottom raised between sides that
 * run to the floor, or the whole case lifted onto levelling feet (every board, opening and datum
 * moves up by the foot height), then the outer sides split into slats when asked.
 */
export function generateCase(input: CaseInput): CaseOutput {
  const base: BaseSpec = input.base ?? (input.plinth > 0 ? { kind: 'plinth', height: input.plinth, setback: 0 } : { kind: 'none' });
  let out: CaseOutput;
  if (base.kind === 'feet') out = lift(caseCore({ ...input, H: input.H - base.height, plinth: 0 }, 0), base.height);
  // sides to the floor: the plinth layout (sides full height, bottom raised) with an open gap, no kick board
  else if (base.kind === 'sides') out = caseCore({ ...input, plinth: base.height }, 0, false);
  else out = caseCore({ ...input, plinth: base.kind === 'plinth' ? base.height : 0 }, base.kind === 'plinth' ? base.setback : 0);
  return input.slats ? slatSides(out, input.slats) : out;
}

/** Everything moved up by `dy` ticks (the case on feet). */
function lift(o: CaseOutput, dy: number): CaseOutput {
  return {
    boards: o.boards.map((b) => ({ ...b, box: { ...b.box, y: b.box.y + dy }, ...(b.datum !== undefined ? { datum: b.datum + dy } : {}) })),
    joints: o.joints.map((j) => ({ ...j, datum: j.datum + dy / 10 })),
    openings: o.openings.map((q) => ({ ...q, y0: q.y0 + dy, y1: q.y1 + dy })),
    datums: o.datums.map((d) => ({ ...d, ticks: d.ticks + dy })),
    columnSpans: o.columnSpans,
    interior: { ...o.interior, y0: o.interior.y0 + dy, y1: o.interior.y1 + dy },
  };
}

const ov1 = (a0: number, a1: number, b0: number, b1: number) => Math.min(a1, b1) - Math.max(a0, b0);
/** The boxes touch or overlap (as substrate validation reads it: within 0.5 tick, sharing area on two axes). */
function meets(a: CaseBox, b: CaseBox): boolean {
  const o = [ov1(a.x, a.x + a.sx, b.x, b.x + b.sx), ov1(a.y, a.y + a.sy, b.y, b.y + b.sy), ov1(a.z, a.z + a.sz, b.z, b.z + b.sz)];
  return o.every((v) => v >= -0.5) && o.filter((v) => v > 0.5).length >= 2;
}

/**
 * Each outer side as a slatted frame (FND-13, kit v1.1.0): front and back stiles one slat wide
 * running the full height, a rail level with the bottom and one level with the top tenoned into
 * them, and slats laid out by the kit's rule (`slatLayout`: exact width and gap, the count follows
 * the space) tenoned into the rails. Every joint the side had goes to each member it meets.
 */
function slatSides(o: CaseOutput, s: SlatSpec): CaseOutput {
  const boards: CaseBoard[] = [];
  const members = new Map<string, CaseBoard[]>();
  const frameJoints: CaseJoint[] = [];
  const bottom = o.boards.find((b) => b.role === 'bottom');
  const top = o.boards.find((b) => b.role === 'top');
  for (const b of o.boards) {
    if (b.role !== 'side' || !bottom || !top) { boards.push(b); continue; }
    const name = b.id === 'side-l' ? 'Left' : 'Right';
    const { x, sx, z, sz, y, sy } = b.box;
    const w = s.width;
    const yB0 = bottom.box.y, yB1 = bottom.box.y + bottom.box.sy;
    const yT0 = top.box.y, yT1 = top.box.y + top.box.sy;
    const stiles: CaseBoard[] = [
      { ...b, id: `${b.id}-stile-b`, role: 'post', label: `${name} side, back stile`, w, box: { x, y, z, sx, sy, sz: w }, machining: 'mortised for two rails' },
      { ...b, id: `${b.id}-stile-f`, role: 'post', label: `${name} side, front stile`, w, box: { x, y, z: z + sz - w, sx, sy, sz: w }, machining: 'mortised for two rails' },
    ];
    const railLen = sz - 2 * w;
    const rails: CaseBoard[] = [
      { ...b, id: `${b.id}-rail-b`, role: 'rail', label: `${name} side, bottom rail`, l: railLen, w: yB1 - yB0, grainWorld: 'z', box: { x, y: yB0, z: z + w, sx, sy: yB1 - yB0, sz: railLen }, machining: 'tenoned into both stiles; mortised for the slats' },
      { ...b, id: `${b.id}-rail-t`, role: 'rail', label: `${name} side, top rail`, l: railLen, w: yT1 - yT0, grainWorld: 'z', box: { x, y: yT0, z: z + w, sx, sy: yT1 - yT0, sz: railLen }, machining: 'tenoned into both stiles; mortised for the slats' },
    ];
    const lay = slatLayout(z + w, railLen, s);
    const slats: CaseBoard[] = lay.map((q, k) => ({
      ...b, id: `${b.id}-slat-${k + 1}`, role: 'slat', label: `${name} side slat ${k + 1} of ${lay.length}`,
      l: yT0 - yB1, w: q.z1 - q.z0, box: { x, y: yB1, z: q.z0, sx, sy: yT0 - yB1, sz: q.z1 - q.z0 }, machining: 'tenoned into both rails',
    }));
    for (const r of rails) for (const st of stiles) frameJoints.push({ a: r.id, b: st.id, kind: 'mortise-tenon', face: 'side', datum: r.box.y / 10 });
    for (const sl of slats) for (const r of rails) frameJoints.push({ a: sl.id, b: r.id, kind: 'mortise-tenon', face: r.id.endsWith('-b') ? 'top' : 'underside', datum: (r.id.endsWith('-b') ? yB1 : yT0) / 10 });
    const all = [...stiles, ...rails, ...slats];
    members.set(b.id, all);
    boards.push(...all);
  }
  if (!members.size) return o;
  const box = new Map(boards.map((b) => [b.id, b.box]));
  const joints: CaseJoint[] = [];
  for (const j of o.joints) {
    const sideA = members.get(j.a), sideB = members.get(j.b);
    if (!sideA && !sideB) { joints.push(j); continue; }
    const other = box.get(sideA ? j.b : j.a);
    for (const q of (sideA ?? sideB)!) if (other && meets(q.box, other)) joints.push(sideA ? { ...j, a: q.id } : { ...j, b: q.id });
  }
  joints.push(...frameJoints);
  return { ...o, boards, joints };
}

function caseCore(input: CaseInput, plinthSetback: number, kick = true): CaseOutput {
  const { W, H, D, t, plinth: p } = input;
  const naming = input.naming ?? DEFAULT_NAMING;
  const cols = input.columns;
  const nCols = cols.length;
  const bk = input.back.kind;
  const b = bk === 'none' ? 0 : input.back.thickness;
  /** Rebate width into the sides, top and bottom for a rebated back. */
  const r = bk === 'rebated' ? Math.round(t / 2) : 0;
  const boards: CaseBoard[] = [];
  const joints: CaseJoint[] = [];
  const openings: CaseOpening[] = [];
  const datums: CaseOutput['datums'] = [];
  const columnSpans: CaseOutput['columnSpans'] = [];

  // Depths: with an overlay back the whole carcass sits in front of it; with a rebated back
  // the sides, top and bottom run full depth (the back sits in their rebate) and the inner
  // parts stop in front of it.
  const outerZ = bk === 'rebated' ? 0 : b;
  const outerDepth = D - outerZ;
  const innerZ = b;
  const innerDepth = D - b;
  const rebateNote = r ? `rebate ${b / 10} × ${r / 10} mm along the back inside edge` : undefined;

  const add = (x: CaseBoard) => { boards.push(x); return x; };
  const join = (a: string, bId: string, kind: JointKind, face: string, datum: number) => joints.push({ a, b: bId, kind, face, datum });

  add({ id: 'side-l', role: 'side', label: 'Left side', l: H, w: outerDepth, t, grainWorld: 'y', box: { x: 0, y: 0, z: outerZ, sx: t, sy: H, sz: outerDepth }, stock: 'carcass', ...(rebateNote ? { machining: rebateNote } : {}) });
  add({ id: 'side-r', role: 'side', label: 'Right side', l: H, w: outerDepth, t, grainWorld: 'y', box: { x: W - t, y: 0, z: outerZ, sx: t, sy: H, sz: outerDepth }, stock: 'carcass', ...(rebateNote ? { machining: rebateNote } : {}) });
  const inner = W - 2 * t;
  add({ id: 'bottom', role: 'bottom', label: 'Bottom', l: inner, w: outerDepth, t, grainWorld: 'x', box: { x: t, y: p, z: outerZ, sx: inner, sy: t, sz: outerDepth }, stock: 'carcass', ...(rebateNote ? { machining: rebateNote } : {}) });
  const cornerKind: JointKind = input.corner === 'mitre' ? 'mitre' : 'screwed-butt';
  join('bottom', 'side-l', cornerKind, 'inner', p / 10);
  join('bottom', 'side-r', cornerKind, 'inner', p / 10);

  const y0 = p + t; // top face of the bottom
  const yTop = H - t; // underside of the top
  const verticalLen = yTop - y0;

  // Columns and full-height dividers
  const verticalIds: string[] = ['side-l'];
  let x = t;
  for (let c = 0; c < nCols; c++) {
    const col = cols[c]!;
    columnSpans.push({ x0: x, x1: x + col.clear });
    x += col.clear;
    if (c < nCols - 1) {
      const n = naming.divider(c + 1, nCols);
      add({ id: n.id, role: 'divider', label: n.label, l: verticalLen, w: innerDepth, t, grainWorld: 'y', box: { x, y: y0, z: innerZ, sx: t, sy: verticalLen, sz: innerDepth }, stock: 'carcass', column: c });
      join(n.id, 'bottom', 'screwed-butt', 'top', y0 / 10);
      join(n.id, 'top', 'screwed-butt', 'underside', yTop / 10);
      verticalIds.push(n.id);
      x += t;
    }
  }
  verticalIds.push('side-r');

  // Shelves, row dividers and openings per column
  const hd = input.shelfJoint === 'housed-dado' ? housingDepth(t) : input.shelfJoint === 'through-tenon' ? t : 0;
  const rdJoint = input.rowDividerJoint ?? 'screwed-butt';
  for (let c = 0; c < nCols; c++) {
    const col = cols[c]!;
    const span = columnSpans[c]!;
    const left = verticalIds[c]!, right = verticalIds[c + 1]!;
    let y = y0;
    let below = 'bottom';
    for (let rIdx = 0; rIdx < col.rows.length; rIdx++) {
      const row = col.rows[rIdx]!;
      const g = row.clear;
      const isLast = rIdx === col.rows.length - 1;
      const above = isLast ? 'top' : naming.shelf(c, rIdx + 1, nCols).id;
      // cells
      const cells = row.cells && row.cells.length > 1 ? row.cells : [col.clear];
      let cx = span.x0;
      cells.forEach((cw, k) => {
        const o = naming.opening(c, rIdx, cells.length > 1 ? k : 0, nCols);
        openings.push({ id: o.id, label: o.label, column: c, row: rIdx, cell: k, x0: cx, x1: cx + cw, y0: y, y1: y + g, clear: g });
        cx += cw;
        if (k < cells.length - 1) {
          const n = naming.rowDivider(c, rIdx, k + 1);
          add({ id: n.id, role: 'divider', label: n.label, l: g, w: innerDepth, t, grainWorld: 'y', box: { x: cx, y, z: innerZ, sx: t, sy: g, sz: innerDepth }, stock: 'carcass', column: c });
          join(n.id, below, rdJoint, 'top', y / 10);
          join(n.id, above, rdJoint, 'underside', (y + g) / 10);
          cx += t;
        }
      });
      y += g;
      if (!isLast) {
        const k = rIdx + 1;
        const n = naming.shelf(c, k, nCols);
        const len = col.clear + 2 * hd;
        add({
          id: n.id, role: 'shelf', label: n.label, l: len, w: innerDepth, t, grainWorld: 'x',
          box: { x: span.x0 - hd, y, z: innerZ, sx: len, sy: t, sz: innerDepth }, stock: 'carcass',
          datum: y, shelfNo: k, column: c,
          ...(hd ? { machining: input.shelfJoint === 'through-tenon' ? `through-tenoned into ${left} and ${right}, tenons ${hd / 10} mm long, wedged` : `housed ${hd / 10} mm into ${left} and ${right}` } : {}),
        });
        join(n.id, left, input.shelfJoint, 'inner', y / 10);
        join(n.id, right, input.shelfJoint, 'inner', y / 10);
        datums.push({ partId: n.id, label: n.label, ticks: y, column: c, shelfNo: k });
        below = n.id;
        y += t;
      }
    }
  }

  add({ id: 'top', role: 'top', label: 'Top', l: inner, w: outerDepth, t, grainWorld: 'x', box: { x: t, y: H - t, z: outerZ, sx: inner, sy: t, sz: outerDepth }, stock: 'carcass', ...(rebateNote ? { machining: rebateNote } : {}) });
  join('top', 'side-l', cornerKind, 'inner', yTop / 10);
  join('top', 'side-r', cornerKind, 'inner', yTop / 10);

  if (p > 0 && kick) {
    add({ id: 'plinth', role: 'plinth', label: 'Plinth', l: inner, w: p, t, grainWorld: 'x', box: { x: t, y: 0, z: D - t - plinthSetback, sx: inner, sy: p, sz: t }, stock: 'carcass' });
    join('plinth', 'side-l', 'screwed-butt', 'inner', 0);
    join('plinth', 'side-r', 'screwed-butt', 'inner', 0);
    join('plinth', 'bottom', 'screwed-butt', 'underside', p / 10);
  }

  // Back: one piece, or split at divider centrelines into the fewest pieces that fit
  if (bk !== 'none') {
    const bx0 = bk === 'rebated' ? t - r : 0;
    const bx1 = bk === 'rebated' ? W - t + r : W;
    const by0 = bk === 'rebated' ? p + t - r : p;
    const by1 = bk === 'rebated' ? H - t + r : H;
    const bh = by1 - by0;
    const cuts: number[] = [];
    const fits = input.backFits;
    if (fits && !fits(bx1 - bx0, bh) && nCols > 1) {
      const centres = boards.filter((q) => q.role === 'divider' && q.box.sy === verticalLen).map((q) => q.box.x + t / 2);
      let start = bx0;
      for (let i = 0; i < centres.length; i++) {
        const next = centres[i + 1] ?? bx1;
        if (!fits(next - start, bh)) { cuts.push(centres[i]!); start = centres[i]!; }
      }
    }
    const edges = [bx0, ...cuts, bx1];
    const pieces = edges.length - 1;
    const backIds: string[] = [];
    for (let k = 0; k < pieces; k++) {
      const n = naming.back(k + 1, pieces);
      const bw = edges[k + 1]! - edges[k]!;
      const vertical = bh >= bw;
      add({ id: n.id, role: 'back', label: n.label, l: vertical ? bh : bw, w: vertical ? bw : bh, t: b, grainWorld: vertical ? 'y' : 'x', box: { x: edges[k]!, y: by0, z: 0, sx: bw, sy: bh, sz: b }, stock: 'back' });
      backIds.push(n.id);
    }
    const backAt = (xMid: number) => {
      for (let k = 0; k < pieces; k++) if (xMid >= edges[k]! - 1 && xMid <= edges[k + 1]! + 1) return backIds[k]!;
      return backIds[0]!;
    };
    const outerKind: JointKind = bk === 'rebated' ? 'rebate' : 'screwed-butt';
    for (const q of boards) {
      if (q.stock === 'back') continue;
      if (q.role === 'plinth') continue;
      const touchesBack = bk === 'overlay' ? q.box.z <= b + 1 : q.box.z <= b + 1 || ['side', 'top', 'bottom'].includes(q.role);
      if (!touchesBack) continue;
      const xs = [q.box.x, q.box.x + q.box.sx];
      const ids = new Set<string>();
      for (const id of backIds) {
        const pb = boards.find((z) => z.id === id)!;
        if (Math.min(xs[1]!, pb.box.x + pb.box.sx) - Math.max(xs[0]!, pb.box.x) > -1) ids.add(id);
      }
      if (!ids.size) ids.add(backAt((xs[0]! + xs[1]!) / 2));
      for (const id of ids) {
        const pb = boards.find((z) => z.id === id)!;
        // must share a face: the back piece's x-range meets the board's x-range
        if (Math.min(xs[1]!, pb.box.x + pb.box.sx) - Math.max(xs[0]!, pb.box.x) < 0) continue;
        const kind = ['side', 'top', 'bottom'].includes(q.role) ? outerKind : 'screwed-butt';
        join(id, q.id, kind, 'back', (q.datum ?? 0) / 10);
      }
    }
  }
  return { boards, joints, openings, datums, columnSpans, interior: { y0, y1: yTop, z0: innerZ, z1: D } };
}
